"use client";

import { useEffect, useState } from "react";
import type {
	PromotionMilestone,
	PromotionProgressStatus
} from "@pamperedchef/consumer-components/cart";

import { getHttpClientConfig } from "@/config/http-client";

export const PROMOTION_PROGRESS_URL =
	"/consumer-cart-service/public/v1/promotionProgress";

export interface PromotionProgress {
	milestones: PromotionMilestone[];
	progress: PromotionProgressStatus;
}

interface PromotionProgressResponse {
	status?: "success" | "failure";
	result?: Partial<PromotionProgress>;
}

function readPromotionProgress(
	body: PromotionProgressResponse | null
): PromotionProgress | null {
	if (body?.status !== "success" || !body.result) return null;
	const { milestones, progress } = body.result;
	if (!Array.isArray(milestones) || !progress) return null;
	return { milestones, progress };
}

export function usePromotionProgress(): PromotionProgress | null {
	const [promotionProgress, setPromotionProgress] =
		useState<PromotionProgress | null>(null);

	useEffect(() => {
		let cancelled = false;

		fetch(PROMOTION_PROGRESS_URL, {
			credentials: "include",
			headers: { Accept: "application/json" },
			signal: AbortSignal.timeout(
				getHttpClientConfig().timeoutSeconds * 1000
			)
		})
			.then((res) => (res.status === 200 ? res.json() : null))
			.then((body: PromotionProgressResponse | null) => {
				if (!cancelled)
					setPromotionProgress(readPromotionProgress(body));
			})
			.catch(() => {
				if (!cancelled) setPromotionProgress(null);
			});

		return () => {
			cancelled = true;
		};
	}, []);

	return promotionProgress;
}
